import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { reportService, ReportFilters, ReportUser, ReportProject } from '@/services/reportService'

interface ReportFiltersProps {
  filters: ReportFilters
  onFiltersChange: (filters: ReportFilters) => void
}

const ReportFiltersComponent: React.FC<ReportFiltersProps> = ({ filters, onFiltersChange }) => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER'
  
  const [users, setUsers] = useState<ReportUser[]>([])
  const [projects, setProjects] = useState<ReportProject[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        const [usersData, projectsData] = await Promise.all([
          isAdmin ? reportService.getUsers() : Promise.resolve([]),
          reportService.getProjects()
        ])
        setUsers(usersData)
        setProjects(projectsData)
      } catch (error) {
        console.error('Failed to load filter data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isAdmin])

  const handleDateRangeChange = (type: 'today' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth') => {
    const dateRange = reportService.getDefaultDateRange(type)
    onFiltersChange({
      ...filters,
      ...dateRange
    })
  }

  const handleProjectChange = (projectId: string, checked: boolean) => {
    const currentProjectIds = filters.projectIds || []
    const newProjectIds = checked
      ? [...currentProjectIds, projectId]
      : currentProjectIds.filter(id => id !== projectId)
    
    onFiltersChange({
      ...filters,
      projectIds: newProjectIds.length > 0 ? newProjectIds : undefined
    })
  }

  return (
    <div className="card p-6 mb-6">
      <h3 className="text-lg font-semibold text-secondary-800 mb-4">篩選條件</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* 用戶選擇器 - 僅管理員可見 */}
        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              選擇用戶
            </label>
            <select
              value={filters.userId || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                userId: e.target.value || undefined
              })}
              className="w-full p-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={loading}
            >
              {/* 當前用戶（自己） */}
              {user && (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email}) - 我的報表
                </option>
              )}
              {/* 全部用戶 */}
              <option value="">全部用戶</option>
              {/* 其他用戶 */}
              {users.filter(u => u.id !== user?.id).map(otherUser => (
                <option key={otherUser.id} value={otherUser.id}>
                  {otherUser.name} ({otherUser.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 開始日期 */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            開始日期
          </label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => onFiltersChange({
              ...filters,
              startDate: e.target.value
            })}
            className="w-full p-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* 結束日期 */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            結束日期
          </label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => onFiltersChange({
              ...filters,
              endDate: e.target.value
            })}
            className="w-full p-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* 狀態篩選 */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            時間記錄狀態
          </label>
          <select
            value={filters.status?.[0] || ''}
            onChange={(e) => onFiltersChange({
              ...filters,
              status: e.target.value ? [e.target.value] : undefined
            })}
            className="w-full p-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">全部狀態</option>
            <option value="STOPPED">已停止</option>
            <option value="APPROVED">已批准</option>
            <option value="SUBMITTED">已提交</option>
            <option value="REJECTED">已拒絕</option>
          </select>
        </div>
      </div>

      {/* 快速日期選擇 */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => handleDateRangeChange('today')}
          className="btn-secondary text-sm"
        >
          今天
        </button>
        <button
          onClick={() => handleDateRangeChange('thisWeek')}
          className="btn-secondary text-sm"
        >
          本週
        </button>
        <button
          onClick={() => handleDateRangeChange('lastWeek')}
          className="btn-secondary text-sm"
        >
          上週
        </button>
        <button
          onClick={() => handleDateRangeChange('thisMonth')}
          className="btn-secondary text-sm"
        >
          本月
        </button>
        <button
          onClick={() => handleDateRangeChange('lastMonth')}
          className="btn-secondary text-sm"
        >
          上月
        </button>
      </div>

      {/* 專案篩選 */}
      {projects.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            專案篩選
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
            {projects.map(project => (
              <label key={project.id} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.projectIds?.includes(project.id) || false}
                  onChange={(e) => handleProjectChange(project.id, e.target.checked)}
                  className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="truncate">{project.name}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 清除篩選 */}
      <div className="flex justify-end mt-4">
        <button
          onClick={() => {
            const defaultRange = reportService.getDefaultDateRange('thisMonth')
            onFiltersChange({
              startDate: defaultRange.startDate,
              endDate: defaultRange.endDate,
              // 管理員清除篩選後預設選擇自己
              ...(isAdmin && user?.id ? { userId: user.id } : {})
            })
          }}
          className="btn-secondary text-sm"
        >
          清除篩選
        </button>
      </div>
    </div>
  )
}

export default ReportFiltersComponent
